import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import {
  AggregatedWholeSaleQueryResultDto,
  BusinessReasonCode,
  ChargeType,
  DataHubStatus,
  MeteringPointType,
  PartyDto,
  PartyService,
  ProcessVariant,
  SettlementMethod,
} from '@app/api/dmc';
import { CardComponent } from '@shared/components/card/card.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { settlementMeteredTable } from '@shared/table-configs/settlement/settlement-metered.table';
import { TableComponent } from '@shared/components/table/table.component';
import { settlementActionMenu } from '@views/settlement/settlement.action-menu';
import {
  SettlementWholesaleViewModalComponent
} from '@shared/modals/settlement/settlement-wholesale-view-modal/settlement-wholesale-view-modal.component';
import {
  SettlementWholesaleRequestModalComponent
} from '@shared/modals/settlement/settlement-wholesale-request-modal/settlement-wholesale-request-modal.component';
import { reorderColumnsByKeys } from '@app/core/utils/reorder-columns-by-keys';
import { settlementWholesaleTableConfig } from '@shared/table-configs/settlement/settlement-wholesale.table';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { SortParam } from '@app/core/constants/sort-param';
import { ODataService } from '@app/core/services/odata/odata.service';
import { ODataType } from '@app/core/services/odata/types/odata-type';

@Component({
  selector: 'app-settlement-aggregated-wholesale',
  imports: [
    CardComponent,
    ButtonDirective,
    TranslatePipe,
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    TableComponent,
    SelectDirective
  ],
  templateUrl: './settlement-aggregated-wholesale.component.html',
  styleUrl: './settlement-aggregated-wholesale.component.css'
})
export class SettlementAggregatedWholesaleComponent {
  @ViewChild('table', {static: false}) public table?: TableComponent<any>;

  public form: FormGroup = new FormGroup({
    startDate: new FormControl(),
    endDate: new FormControl(),
    settlementMethod: new FormControl(),
    businessReasonCode: new FormControl(),
    processVariant: new FormControl(),
    dataHubStatus: new FormControl(),
    chargeType: new FormControl(),
    chargeTypeId: new FormControl(),
    balanceSupplierIdentification: new FormControl(),
    typeOfMeteringPoint: new FormControl(),
    chargeOwnerEnergyParty: new FormControl(),
  });

  /** Actions **/
  public actions: MenuAction[] = [
    {
      ...settlementActionMenu[0],
      callback: () => this.openViewModal(),
      disabled: () => this.selectedElements.length !== 1,
    },
    {
      ...settlementActionMenu[1],
      callback: () => this.openRequestModal(),
    }
  ]

  /** Table **/
  public tableConfig: TableConfig<AggregatedWholeSaleQueryResultDto> = {
    columns: [
      ...settlementMeteredTable.columns,
      ...settlementWholesaleTableConfig,
    ],
    id: 'settlement-aggregated-wholesale-table',
    title: 'settlement.wholesale.title',
    selectable: true,
    selectableType: 'single',
    exportable: true,
    copyable: true,
    floatingHeader: true,
    saveChanges: true,
    oData: true,
    pageSizeSelection: true,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.searchCallback(pageSize, page, order),
    contextActions: [
      this.actions[0],
    ]
  }

  /** Enums **/
  public balanceSupplierList: KeyValue[] = [];
  public meteringPointTypeList: KeyValue[] = sortByTranslation(enumToKeyValue(MeteringPointType, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.meteringpoint.meteringpointtype.'
  }));
  public settlementMethodList: KeyValue[] = enumToKeyValue(SettlementMethod, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.settlementmethod.'
  });
  public chargeTypeList: KeyValue[] = enumToKeyValue(ChargeType, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.chargetype.'
  });
  public chargeOwnerEnergyPartyList: KeyValue[] = [];
  public businessReasonCodeList: KeyValue[] = enumToKeyValue(BusinessReasonCode, {
    includeKeys: ['SecondSettlement', 'CorrectionSettlement'],
    prefixValue: 'enum.settlement.businessreasoncode.'
  });
  public processVariantList: KeyValue[] = enumToKeyValue(ProcessVariant, {
    omitKeys: ['NotSpecified', 'FirstSettlement', 'SecondSettlement'],
    prefixValue: 'enum.settlement.processvariant.'
  });
  public dataHubStatusList: KeyValue[] = enumToKeyValue(DataHubStatus, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.datahub.status.'
  });

  /** Carriers **/
  private balanceSuppliers: PartyDto[] = [];
  private chargeInformationParties: PartyDto[] = [];

  /** Selected Elements **/
  public selectedElements: AggregatedWholeSaleQueryResultDto[] = [];

  constructor(
    private readonly modalService: ModalService,
    private readonly headerMenuService: HeaderService,
    private readonly oDataService: ODataService,
    private readonly partyService: PartyService,
  ) {
    this.headerMenuService.setActions(this.actions);

    /** Reorganize Table **/
    this.tableConfig.columns = reorderColumnsByKeys<AggregatedWholeSaleQueryResultDto>(
      this.tableConfig.columns,
      [
        'msgCreatedTS',
        'startDate',
        'endDate',
        'balanceSupplierIdentification',
        'businessReasonCode',
        'processVariant',
        'resolutionDuration',
        'productIdentification',
        'unitType',
        'typeOfMeteringPoint',
        'chargeType',
        'chargeTypeId',
        'chargeOwnerEnergyParty',
        'settlementMethod',
        'version',
        'dataHubStatus',
      ]
    );

    /** Set Menu **/
    this.headerMenuService.setActions(this.actions,
      {
        view: () => this.openViewModal(),
        request: () => this.openRequestModal(),
      },
      {
        view: () => this.selectedElements.length !== 1,
      }
    )

    /** Populate Necessary Lists **/
    this.partyService.partyGetBalanceSuppliersGet().subscribe((data: PartyDto[]) => {
      this.balanceSuppliers = data ?? [];

      this.balanceSupplierList = this.balanceSuppliers.map((item: PartyDto) => ({
        key: item.identification ?? '',
        value: item.name ?? '',
      }))
    });

    this.partyService.partyGetChargeInformationPartiesGet().subscribe((data: PartyDto[]) => {
      this.chargeInformationParties = data ?? [];

      this.chargeOwnerEnergyPartyList = this.chargeInformationParties.map((item: PartyDto) => ({
        key: item.identification ?? '',
        value: item.name ?? '',
      }))
    });
  }

  public searchCallback(pageSize: number, page: number, sort?: SortParam) {
    const data = this.form.value;

    const query = this.oDataService.dmc.query()
      .paginate(page, pageSize)
      .count();

    if (data.startDate)                   query.param('StartDate',                   data.startDate);
    if (data.endDate)                     query.param('EndDate',                     data.endDate);
    if (data.balanceSupplierIdentification) query.param('BalanceSupplierIdentification', data.balanceSupplierIdentification);
    if (data.typeOfMeteringPoint)         query.param('TypeOfMeteringPoint',         data.typeOfMeteringPoint);
    if (data.settlementMethod)            query.param('SettlementMethod',            data.settlementMethod);
    if (data.businessReasonCode)          query.param('BusinessReasonCode',          data.businessReasonCode);
    if (data.processVariant)              query.param('ProcessVariant',              data.processVariant);
    if (data.dataHubStatus)               query.param('DataHubStatus',               data.dataHubStatus);
    if (data.chargeType)                  query.param('ChargeType',                  data.chargeType);
    if (data.chargeTypeId)                query.param('ChargeTypeId',                data.chargeTypeId);
    if (data.chargeOwnerEnergyParty)      query.param('ChargeOwnerEnergyParty',      data.chargeOwnerEnergyParty);

    if (sort) {
      query.orderBy(`${sort.sortField} ${sort.sortDirection}`);
    }

    return this.oDataService.dmc.getCollection<AggregatedWholeSaleQueryResultDto>(
      ODataType.queryAggregatedWholesale,
      query,
    );
  }

  public openViewModal(): void {
    if (this.selectedElements.length === 1) {
      this.modalService.show({
        component: SettlementWholesaleViewModalComponent,
        title: 'settlement.wholesale.view.title',
        actions: [
          {type: 'secondary', label: 'system.button.close', closeOnClick: true},
        ],
        params: {
          id: this.selectedElements[0].aggregatedWholeSaleId
        }
      })
    }
  }

  public openRequestModal(): void {
    this.modalService.show({
      component: SettlementWholesaleRequestModalComponent,
      title: 'settlement.wholesale.request.title',
      actions: [
        {type: 'primary', label: 'system.button.request', method: 'submit'},
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: {
        monthlyAggregations: false,
      },
    })
  }

}
